/**
 * <g-note-card> - presentational note tile for <g-notes>.
 *
 * Driven entirely by the `.note` view-model the parent sets (already-rendered
 * safe body HTML, color, pin state, colour/confirm UI flags). It observes
 * NO attributes and emits NO events — the parent <g-notes> owns the whole
 * interaction contract via event delegation. (The parent sets a plain
 * `note-id` attribute for event lookup.)
 *
 * The tile has no edit mode of its own: clicking it opens the parent's edit
 * modal, which lives outside the grid so a re-render cannot destroy what you
 * are typing. Rows the tile owns but the click must NOT open — the toolbar,
 * the swatch row, the delete-confirm row — carry `data-noopen`.
 *
 * @attr {string} note-id - set by the parent; used for event lookup only
 */

// Hue anchors, NOT finished surface colours.
//
// These used to be fixed pale tints with a forced dark foreground, on the
// assumption that a note tile is always light. That assumption breaks the
// moment the app is on a dark theme: pastel tiles glare against a dark board
// and stop matching every other surface on the page.
//
// So a note stores a *hue* and the surface is derived at render time by mixing
// that hue into the theme's own `base-100`. A note keeps its identity — "the
// red one" is still red in both themes — while its lightness follows the theme,
// and the foreground can simply inherit `base-content` instead of being pinned.
export const NOTE_HUES = {
  default: '',
  gray:   'oklch(0.62 0.02 250)',
  red:    'oklch(0.62 0.20 25)',
  orange: 'oklch(0.70 0.17 70)',
  yellow: 'oklch(0.80 0.16 100)',
  green:  'oklch(0.68 0.17 150)',
  cyan:   'oklch(0.70 0.12 200)',
  blue:   'oklch(0.62 0.18 255)',
  purple: 'oklch(0.60 0.20 300)',
  pink:   'oklch(0.65 0.20 350)',
};

export const NOTE_PALETTE = Object.keys(NOTE_HUES);

// Tile surface: a light wash of the hue over the theme's card colour.
export function noteTintStyle(color) {
  const hue = NOTE_HUES[color];
  return hue ? `background-color:color-mix(in oklab, ${hue} 18%, var(--color-base-100));` : '';
}

// Swatch: the same hue, but mixed far stronger. A 20px circle at the tile's own
// 18% would be indistinguishable from its neighbours — a swatch has to identify
// the colour, not preview the exact surface.
export function noteSwatchStyle(color) {
  const hue = NOTE_HUES[color];
  return hue ? `background-color:color-mix(in oklab, ${hue} 60%, var(--color-base-100));` : '';
}

export class GNoteCard extends HTMLElement {
  constructor() {
    super();
    this._note = null;
  }

  connectedCallback() { this.render(); }

  get note() { return this._note; }
  set note(v) {
    this._note = v && typeof v === 'object' ? v : null;
    if (this.isConnected) this.render();
  }

  render() {
    const n = this._note;
    if (!n) { this.innerHTML = ''; return; }

    const tint = noteTintStyle(n.color);
    // `select-none`: the whole tile is the drag target, and a text selection
    // begun on the tile would swallow the gesture — the browser drags the
    // selection instead of the card, and reordering silently stops working.
    // Killing selection on the tile is what lets the drag start immediately on
    // pointer movement with no press-and-hold delay. Edit mode re-enables it
    // (`select-text`) on the fields that need it.
    // `relative`: the pin is absolutely positioned against the card corner.
    // `cursor-pointer` sits on the tile, not the body: the whole tile is the
    // edit affordance now, so the pointer has to say so over the padding and the
    // title too. The chrome rows below take `cursor-default` back.
    this.classList.add('group', 'relative', 'break-inside-avoid', 'mb-4', 'block', 'rounded-lg', 'border', 'border-base-300', 'shadow-sm', 'p-3', 'select-none', 'cursor-pointer');
    this.setAttribute('style', tint);

    const palette = Array.isArray(n.palette) && n.palette.length ? n.palette : NOTE_PALETTE;

    // The pin is positioned against the CARD, not against the title. It used to
    // sit in a flex row with the title, which meant a note with no title still
    // had to render that row — with an empty spacer standing in for the missing
    // title — just to keep the pin in the corner. That spacer was the blank band
    // at the top of every untitled note.
    const pinReveal = n.pinned ? '' : ' opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100';
    const pin = `<button type="button" data-role="note-pin" class="btn btn-ghost btn-xs btn-circle absolute top-1.5 right-1.5 z-10 transition-opacity${pinReveal}" title="${n.pinned ? 'Unpin' : 'Pin'}">
      <i class="fa-${n.pinned ? 'solid' : 'regular'} fa-thumbtack"></i></button>`;

    // No title → no title element at all, so the body starts at the top of the
    // card. Whichever element IS first has to keep clear of the pin's corner.
    const hasTitle = n.title != null && String(n.title) !== '';
    const title = hasTitle
      ? `<div class="font-medium leading-snug pr-7">${escapeHtml(String(n.title))}</div>`
      : '';

    const colorRow = n.showColors
      ? `<div data-noopen class="flex flex-wrap gap-1 mt-2 cursor-default">${palette.map(key => `
          <button type="button" data-role="note-swatch" data-color="${escapeHtml(key)}" title="${escapeHtml(key)}"
            class="w-5 h-5 rounded-full border border-base-content/20${key === 'default' ? ' bg-base-100' : ''}"
            style="${noteSwatchStyle(key)}"></button>`).join('')}</div>`
      : '';

    const confirm = n.confirmDelete
      ? `<div data-noopen class="flex items-center gap-2 mt-2 text-xs cursor-default">
          <span>Delete note?</span>
          <button type="button" data-role="note-delete-confirm" class="btn btn-error btn-xs">Delete</button>
          <button type="button" data-role="note-delete-cancel" class="btn btn-ghost btn-xs">Cancel</button>
        </div>` : '';

    const toolbar = n.readonly ? '' : `
      <div data-noopen class="flex items-center gap-0.5 mt-2 pt-2 border-t border-base-content/10 cursor-default">
        <button type="button" data-role="note-color" class="btn btn-ghost btn-xs btn-circle" title="Colour"><i class="fa-regular fa-palette"></i></button>
        <button type="button" data-role="note-delete" class="btn btn-ghost btn-xs btn-circle" title="Delete"><i class="fa-regular fa-trash"></i></button>
        ${n.updatedLabel ? `<span class="ml-auto text-[10px] opacity-60">${escapeHtml(n.updatedLabel)}</span>` : ''}
      </div>`;

    this.innerHTML = `
      ${pin}
      ${title}
      <div data-role="note-body" class="g-note-body text-sm break-words${hasTitle ? ' mt-1' : ' pr-7'}">${n.bodyHtml || ''}</div>
      ${colorRow}
      ${confirm}
      ${toolbar}`;
  }
}

// The five-button WYSIWYG strip, shared by the composer and the edit modal in
// <g-notes> so "writing a note" looks identical either way.
//
// It carries its own full-width surface: everything around it is transparent so
// the note's colour shows through, which left the buttons floating on the tint
// with nothing to sit on. `bg-base-content/10` is a wash rather than a colour —
// it darkens a light theme and lightens a dark one, and reads as a strip over
// any of the ten note hues without needing a variant per colour.
export function formatToolbar() {
  return `
    <div class="flex flex-wrap items-center gap-0.5 w-full rounded bg-base-content/10 px-1.5 py-1">
      ${fmtBtn('bold', '<i class="fa-solid fa-bold"></i>', 'Bold')}
      ${fmtBtn('italic', '<i class="fa-solid fa-italic"></i>', 'Italic')}
      ${fmtBtn('formatBlock', 'H1', 'Heading 1', 'h1')}
      ${fmtBtn('formatBlock', 'H2', 'Heading 2', 'h2')}
      ${fmtBtn('insertUnorderedList', '<i class="fa-solid fa-list-ul"></i>', 'Bullet list')}
    </div>`;
}

// A toolbar button. `label` is renderer-controlled HTML (an icon or short text);
// the parent <g-notes> handles the click on mousedown so the editor keeps focus.
function fmtBtn(cmd, label, title, arg) {
  return `<button type="button" data-role="note-fmt" data-cmd="${cmd}"${arg ? ` data-arg="${arg}"` : ''}` +
    ` class="btn btn-ghost btn-xs btn-square" title="${escapeHtml(title)}">${label}</button>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-note-card')) {
  customElements.define('g-note-card', GNoteCard);
}
